import { data, isRouteErrorResponse, redirect, useLoaderData, useNavigation, useParams, useRouteError } from 'react-router';
import { useAsync, DateCell, StatusPill, ErrorAlert, JsonDetails } from '../components/ui.jsx';
import RelationList from '../components/RelationList.jsx';
import RelatedCompanies from '../components/RelatedCompanies.jsx';
import LineChart from '../components/LineChart.jsx';
import CompanyHistory from '../components/CompanyHistory.jsx';
import { useEffect, useState } from 'react';
import { findCompany, financialsOf, companiesWithParticipant, requestReports, reportRequest, requestCompany, companyRequest, RELATION_LIMIT } from '../data/db.js';
import { cvrOfId } from '../data/queue.js';
import { participants, engagements, companyName, companyPath, companyRedirect } from '../data/relations.js';
import { financialYears, hasFinancials } from '../data/financials.js';
import { currentOf, currentAddress } from '../data/entity.js';
import { lifetime, thousands } from '../data/format.js';
import { companyDescription, notFoundMeta, pageMeta } from '../seo.js';

// One axis: all four series are amounts in DKK. The employees are a count, so
// they are in the table only.
const SERIES = [
  { key: 'grossProfit', label: 'Bruttofortjeneste', color: 'var(--series-1)' },
  { key: 'employeeExpenses', label: 'Personaleudgifter', color: 'var(--series-2)' },
  { key: 'profitLoss', label: 'Resultat', color: 'var(--series-3)' },
  { key: 'equity', label: 'Egenkapital', color: 'var(--series-4)' },
];

// "Fetch latest reports": the company goes into the agent's report queue, and the
// next agent run fetches its annual reports (owner, 2026-09-15; the old cvrweb
// page sent the same request to the openiap queue "reloadcvr").
function ReportRequest({ company }) {
  const [tick, setTick] = useState(0);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const queued = useAsync(() => reportRequest(company.enhedsNummer), [company.enhedsNummer, tick]);

  async function onClick() {
    setSending(true);
    setSendError(null);
    try {
      await requestReports(company);
      setTick((n) => n + 1);
    } catch (error) {
      setSendError(error);
    } finally {
      setSending(false);
    }
  }

  const waiting = queued.data != null;
  let label = 'Fetch latest reports';
  if (sending) label = 'Adding…';
  else if (waiting) label = 'Queued for the next agent run';
  return (
    <div className="report-request">
      <ErrorAlert error={sendError ?? queued.error} />
      <button type="button" className="btn" onClick={onClick} disabled={sending || waiting || queued.data === undefined}
        title={waiting ? 'The agent fetches the latest reports of this company in its next run.' : 'Ask the agent for the latest annual reports of this company.'}>
        {label}
      </button>
    </div>
  );
}

// "Fetch this company from CVR" (owner, 2026-09-26): a link can point to a company
// that kinedb does not have. The CVR number goes into the agent's company queue. The
// agent checks the queue every 30 s. The page reads the row every 5 s, and it
// loads again when the agent has removed the row.
const COMPANY_POLL_MS = 5000;
const MAX_ATTEMPTS = 3;

function CompanyRequest({ cvr }) {
  const [tick, setTick] = useState(0);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  // True after a read found a waiting row. useAsync keeps the old data while it reads
  // again, so only a finished read with no row may load the page again.
  const [seen, setSeen] = useState(false);
  const queued = useAsync(() => companyRequest(cvr), [cvr, tick]);
  const row = queued.data;
  const waiting = row != null && (row.attempts ?? 0) < MAX_ATTEMPTS;
  const failed = row != null && !waiting;

  // Read the row again while it waits. The row is gone when the agent is done.
  useEffect(() => {
    if (!waiting) return undefined;
    const timer = setTimeout(() => setTick((n) => n + 1), COMPANY_POLL_MS);
    return () => clearTimeout(timer);
  }, [waiting, tick]);

  useEffect(() => {
    if (waiting) setSeen(true);
  }, [waiting]);

  useEffect(() => {
    if (seen && !queued.loading && queued.data === null) window.location.reload();
  }, [seen, queued.loading, queued.data]);

  async function onClick() {
    setSending(true);
    setSendError(null);
    try {
      await requestCompany(cvr);
      setTick((n) => n + 1);
    } catch (error) {
      setSendError(error);
    } finally {
      setSending(false);
    }
  }

  let label = 'Fetch this company from CVR';
  if (sending) label = 'Adding…';
  else if (waiting) label = 'Waiting for the agent…';
  else if (failed) label = 'Try again';
  return (
    <div className="report-request">
      <ErrorAlert error={sendError ?? queued.error} />
      {failed && <p className="muted">{`The agent could not get this company: ${row.lastError ?? 'no reason given'}.`}</p>}
      {waiting && <p className="muted">The agent looks at the queue every 30 seconds. This page loads again when the company is stored.</p>}
      <button type="button" className="btn" onClick={onClick} disabled={sending || waiting || queued.data === undefined}
        title="Ask the agent to get this company from the CVR register.">
        {label}
      </button>
    </div>
  );
}

function Financials({ company }) {
  const years = useAsync(
    async () => (hasFinancials(company.virksomhedsformkort) ? financialYears(await financialsOf(company.enhedsNummer)) : null),
    [company.enhedsNummer],
  );

  let body;
  if (years.error) body = <ErrorAlert error={years.error} />;
  else if (years.data === undefined) body = <p className={hasFinancials(company.virksomhedsformkort) ? 'muted reserve-chart' : 'muted'}>Loading…</p>;
  else if (years.data === null) body = <p className="muted">A company of the form {company.virksomhedsformkort} files no annual report.</p>;
  else if (years.data.length === 0) body = <p className="muted">No annual report is loaded for this company.</p>;
  else {
    body = (
      <div className="stack">
        <LineChart rows={[...years.data].reverse()} xKey="year" series={SERIES} label={`The annual results of ${companyName(company)}`} />
        <div className="table-scroll">
          <table className="data">
            <thead>
              <tr>
                <th>År</th>
                {SERIES.map((s) => <th key={s.key} className="num">{s.label}</th>)}
                <th className="num">Selskabskapital</th>
                <th className="num">Ansatte</th>
              </tr>
            </thead>
            <tbody>
              {years.data.map((y) => (
                <tr key={`${y.start}|${y.end}`}>
                  <td title={`${y.start} – ${y.end}`}>{y.year}</td>
                  {SERIES.map((s) => <td key={s.key} className="num">{thousands(y[s.key])}</td>)}
                  <td className="num">{thousands(y.contributedCapital)}</td>
                  <td className="num">{thousands(y.employees)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <section className="card">
      <div className="card-head">
        <h2>Annual reports</h2>
        {years.data?.length > 0 && <span className="count">{years.data.length} {years.data.length === 1 ? 'year' : 'years'}</span>}
      </div>
      <div className="card-body">
        {body}
        {hasFinancials(company.virksomhedsformkort) && <ReportRequest company={company} />}
      </div>
    </section>
  );
}

function Ownership({ company }) {
  const companies = useAsync(() => companiesWithParticipant(company.enhedsNummer), [company.enhedsNummer]);
  const list = companies.data ? engagements(companies.data, company.enhedsNummer) : null;
  return (
    <section className="card">
      <div className="card-head">
        <h2>Participates in</h2>
        {companies.data && <span className="count">{companies.data.length} companies</span>}
      </div>
      <div className="card-body">
        <ErrorAlert error={companies.error} />
        {!list && !companies.error && <p className="muted">Loading…</p>}
        {list && <RelationList active={list.active} inactive={list.inactive} empty="This company takes part in no other company." />}
        {companies.data?.length === RELATION_LIMIT && <p className="muted">The list stops at {RELATION_LIMIT} companies.</p>}
      </div>
    </section>
  );
}

// The server gets the company before it sends the page, so the HTML holds its name,
// title and canonical for Google. The URL of a company uses its CVR number. An old
// link with the enhedsnummer moves for good (301) to the CVR form.
export async function loader({ params }) {
  const company = await findCompany(params.id);
  if (!company) throw data(null, { status: 404 });
  const path = companyRedirect(company, params.id);
  if (path) throw redirect(path, 301);
  return { company };
}

export function meta({ data: loaded, error }) {
  if (error || !loaded) return notFoundMeta();
  const { company } = loaded;
  return pageMeta({
    title: `${companyName(company)} – CVR ${company.cvr ?? company.enhedsNummer}`,
    description: companyDescription(company),
    path: companyPath(company),
  });
}

export function ErrorBoundary() {
  const error = useRouteError();
  const { id } = useParams();
  if (isRouteErrorResponse(error) && error.status === 404) {
    const cvr = cvrOfId(id);
    return (
      <div className="stack">
        <div className="alert error">{`No company has the number ${id}.`}</div>
        {cvr != null && <CompanyRequest cvr={cvr} />}
      </div>
    );
  }
  return <ErrorAlert error={error} />;
}

export default function CompanyPage() {
  const { company } = useLoaderData();
  const loading = useNavigation().state === 'loading';

  const people = participants(company);
  const branche = currentOf(company.hovedbranche);
  const formerNames = (company.navne ?? []).slice(0, -1).map((n) => n.navn).reverse();
  const ended = company.ophoersDato && company.cvrstatus !== 'NORMAL' && company.cvrstatus !== 'AKTIV';

  return (
    <div className={loading ? 'is-loading' : undefined}>
      <header className="entity-head">
        <div className="eyebrow">Company · CVR {company.cvr}</div>
        <h1>{companyName(company)}</h1>
        <div className="pills">
          <StatusPill status={company.cvrstatus} />
          {company.virksomhedsformlang && <span className="pill plain">{company.virksomhedsformlang}</span>}
        </div>
        {formerNames.length > 0 && <p className="names">Former names: {formerNames.join(' · ')}</p>}
        <dl className="facts">
          <div><dt>Stiftet</dt><dd>{company.stiftelsesDato ? String(company.stiftelsesDato).slice(0, 10) : '—'}</dd></div>
          {ended && <div><dt>Ophørt</dt><dd>{String(company.ophoersDato).slice(0, 10)}</dd></div>}
          <div><dt>Levetid</dt><dd>{lifetime(company.stiftelsesDato, company.ophoersDato) || '—'}</dd></div>
          <div><dt>Branche</dt><dd>{branche?.branchetekst ?? '—'}</dd></div>
          <div><dt>Address</dt><dd>{currentAddress(company.beliggenhedsadresse) || '—'}</dd></div>
          <div><dt>Enhedsnummer</dt><dd>{company.enhedsNummer}</dd></div>
          <div><dt>Opdateret</dt><dd><DateCell value={company.sidstOpdateret} /></dd></div>
        </dl>
      </header>

      <Financials company={company} />

      <section className="card">
        <div className="card-head">
          <h2>Participants</h2>
          <span className="count">{people.active.length + people.inactive.length}</span>
        </div>
        <div className="card-body">
          <RelationList active={people.active} inactive={people.inactive} empty="No participant is registered." />
        </div>
      </section>

      <Ownership company={company} />
      <RelatedCompanies enhedsNummer={company.enhedsNummer} />
      <CompanyHistory company={company} />
      <JsonDetails value={company} />
    </div>
  );
}
